import type { SeoMeta } from "./config";
import { SITE_NAME } from "./site";

type TagTarget = "name" | "property";

function findMeta(attr: TagTarget, key: string): HTMLMetaElement | null {
  return document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
}

function ensureMeta(attr: TagTarget, key: string): HTMLMetaElement {
  let el = findMeta(attr, key);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMetaContent(attr: TagTarget, key: string, content: string): void {
  const el = ensureMeta(attr, key);
  el.setAttribute("content", content);
}

function removeMeta(attr: TagTarget, key: string): void {
  const el = findMeta(attr, key);
  if (el) el.remove();
}

function setTitle(title: string): void {
  document.title = title;
}

function setCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Applies the resolved SEO metadata for an indexable route and restores or
 * replaces every managed tag so nothing from a previous route can leak.
 */
export function applySeo(meta: SeoMeta, siteUrl: string): void {
  const url = `${siteUrl}${meta.canonicalPath}`;

  setTitle(meta.title);
  setMetaContent("name", "description", meta.description);
  setMetaContent("name", "keywords", meta.keywords);
  setCanonical(url);

  setMetaContent("property", "og:title", meta.title);
  setMetaContent("property", "og:description", meta.description);
  setMetaContent("property", "og:url", url);
  setMetaContent("property", "og:type", meta.ogType);
  setMetaContent("property", "og:site_name", SITE_NAME);

  setMetaContent("name", "twitter:card", "summary_large_image");
  setMetaContent("name", "twitter:title", meta.title);
  setMetaContent("name", "twitter:description", meta.description);

  if (meta.image) {
    setMetaContent("property", "og:image", meta.image);
    setMetaContent("name", "twitter:image", meta.image);
  } else {
    removeMeta("property", "og:image");
    removeMeta("name", "twitter:image");
  }

  // Clear any stale noindex directive from a previous non-indexable route.
  removeMeta("name", "robots");
}

/**
 * Marks a non-indexable route (e.g. the 404 page) so search engines do not
 * index it and clears article/home social metadata.
 */
export function applyNoindex(): void {
  setMetaContent("name", "robots", "noindex, nofollow");
  removeMeta("property", "og:image");
  removeMeta("name", "twitter:image");
}